import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Campaign, Production } from '@/api/types';
import { Icons } from './primitives';
import { ResponsivePicker } from './responsive-picker';

interface ScopeSearchPickerProps<T> {
  items: T[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  getKey: (item: T) => string;
  getSearchText: (item: T) => string;
  renderRow: (item: T, inMenu: boolean) => ReactNode;
  label?: string;
}

/**
 * Canonical searchable scope control shared by the reporting screens and
 * Creative Library. Keeping it here avoids importing an entire report screen
 * just to render the picker.
 */
export function ScopeSearchPicker<T>({
  items,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  getKey,
  getSearchText,
  renderRow,
  label,
}: ScopeSearchPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = items.find((item) => getKey(item) === value);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? items.filter((item) => getSearchText(item).toLowerCase().includes(needle))
    : items;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
          setQ('');
        }}
        style={{ padding: '6px 10px', minWidth: 300, justifyContent: 'space-between', gap: 8 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
              {label}
            </span>
          )}
          {selected
            ? renderRow(selected, false)
            : <span style={{ color: 'var(--ink-3)' }}>{placeholder || 'Select…'}</span>}
        </span>
        <span style={{ color: 'var(--ink-3)' }}>{Icons.chev_d}</span>
      </button>

      {open && (
        <ResponsivePicker label={label || placeholder || 'Choose a scope'} onClose={() => setOpen(false)}>
        <div className="scope-search-pop" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, minWidth: 360, background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>{Icons.search}</span>
            <input
              autoFocus
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={searchPlaceholder || 'Search…'}
              aria-label={searchPlaceholder || 'Search scopes'}
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--ink-1)' }}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                style={{ border: 'none', background: 'transparent', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 12 }}
              >
                clear
              </button>
            )}
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', padding: 4 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 10px', color: 'var(--ink-3)', fontSize: 12 }}>
                No results for “{q}”
              </div>
            ) : filtered.map((item) => {
              const key = getKey(item);
              const isSelected = value === key;
              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onKeyDown={event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();onChange(key);setOpen(false);}}}
                  onClick={() => {
                    onChange(key);
                    setOpen(false);
                  }}
                  className={`dd-item ${isSelected ? 'on' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 4 }}
                >
                  <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {renderRow(item, true)}
                  </span>
                  {isSelected && <span style={{ color: 'var(--accent, var(--ink-1))' }}>{Icons.check}</span>}
                </div>
              );
            })}
          </div>
        </div>
        </ResponsivePicker>
      )}
    </div>
  );
}

export function renderCampaignRow(
  campaign: Campaign,
  productionMap: ReadonlyMap<string, Production>,
) {
  const production = productionMap.get(campaign.production_id);
  return (
    <>
      <span style={{ width: 8, height: 10, borderRadius: 1, background: 'var(--chart-1)', display: 'inline-block', flex: '0 0 auto' }}/>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {campaign.name}
        {production && <span style={{ color: 'var(--ink-3)' }}> — {production.name}</span>}
      </span>
    </>
  );
}

export function renderProductionRow(production: Production) {
  return (
    <>
      <span style={{ width: 8, height: 10, borderRadius: 1, background: 'var(--accent)', display: 'inline-block', flex: '0 0 auto' }}/>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{production.name}</span>
    </>
  );
}
